import { useEffect, useRef, useState } from "react";

type Options = {
  /** 0 — 1, how much of the element must be visible before triggering. */
  threshold?: number;
  /** Margin around the root box (CSS-style). */
  rootMargin?: string;
  /** When true, only fire once and disconnect. Default true. */
  once?: boolean;
};

/**
 * Tiny IntersectionObserver hook for scroll-reveal animations.
 * Returns a ref to attach + a boolean indicating whether the element
 * has entered the viewport at least once.
 */
export function useInView<T extends HTMLElement = HTMLElement>(
  options: Options = {},
) {
  const { threshold = 0.15, rootMargin = "0px 0px -10% 0px", once = true } = options;
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect users who prefer reduced motion — show content right away.
    if (typeof window !== "undefined") {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      if (mq.matches) {
        setInView(true);
        return;
      }
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) io.disconnect();
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, inView };
}